import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Keeps the free gift line in step with The Heir Set.
 *
 * Config comes from <script id="hh-gift-config"> ({ variantId, min, excludeHandles }).
 * After every cart change (and once when the page loads) it reads the cart:
 * while it holds at least `min` watches it keeps exactly one gift, otherwise
 * none. Products in `excludeHandles` (The Heir Set, which includes the gift)
 * don't count as watches. Changes are posted to /cart/update.js and announced with Horizon's
 * cart event, so the cart drawer and cart page re-render.
 */
const config = (() => {
  try {
    return JSON.parse(document.getElementById('hh-gift-config')?.textContent ?? 'null');
  } catch {
    return null;
  }
})();

const SOURCE = 'hh-gift-sync';
let running = false;
let queued = false;

const routes = () => {
  const r = typeof Theme !== 'undefined' ? Theme.routes : null;
  return { cart: r?.cart_url ?? '/cart', update: r?.cart_update_url ?? '/cart/update.js' };
};

async function sync() {
  if (!config?.variantId) return;
  if (running) {
    queued = true;
    return;
  }
  running = true;

  try {
    const { cart: cartUrl, update } = routes();
    const cart = await (await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
    const giftId = Number(config.variantId);

    const excluded = new Set((config.excludeHandles ?? []).map((handle) => String(handle).trim()).filter(Boolean));
    let watches = 0;
    let gifts = 0;
    for (const item of cart.items ?? []) {
      if (Number(item.variant_id) === giftId) gifts += item.quantity;
      else if (!excluded.has(item.handle)) watches += item.quantity;
    }

    const wanted = watches >= (Number(config.min) || 2) ? 1 : 0;
    if (gifts === wanted) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: wanted ? 'add' : 'remove',
        context: 'cart',
        lines: [{ merchandiseId: String(giftId), quantity: wanted }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: { [giftId]: wanted }, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          source: SOURCE,
          sourceId: SOURCE,
          items: data.items,
          itemCount: data.item_count,
          sections: data.sections,
          didError: Boolean(data.status),
        },
      });
    } catch (error) {
      deferred.reject(error);
    }
  } catch (error) {
    console.warn('[hh-gift-sync]', error);
  } finally {
    running = false;
    if (queued) {
      queued = false;
      sync();
    }
  }
}

if (config?.variantId && !window.hhGiftSyncReady) {
  window.hhGiftSyncReady = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {{promise?: Promise<any>}} */ (event).promise;
    Promise.resolve(promise)
      .then((result) => {
        if (result?.detail?.source === SOURCE) return;
        sync();
      })
      .catch(() => {});
  });

  // Catch carts changed elsewhere (another tab, an old session).
  const idle = window.requestIdleCallback ?? ((callback) => window.setTimeout(callback, 1500));
  idle(() => sync());
}
